import React, { ReactElement, useState } from "react"
import { useTranslation } from "react-i18next"
import { Form, Input, message, Typography } from "antd"
import ModalCard from "@/UI/Modals/ModalCard"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClone, faExclamationCircle } from "@fortawesome/free-solid-svg-icons"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import { IApiKeys } from "@/types/credentials.types"
import { isEmpty } from "lodash"
import { SemiBoldLabelText } from "@/UI"

interface IAPIKeysAddEditModalProps {
  title: string
  button: ReactElement
  handleCreateKeys: (keyName: string) => Promise<IApiKeys>
  fetchCredentials: () => Promise<void>
}

const APIKeysAddEditModal: React.FC<IAPIKeysAddEditModalProps> = ({
  title,
  button,
  fetchCredentials,
  handleCreateKeys,
}) => {
  const { t } = useTranslation()
  const { Paragraph } = Typography
  const [form] = Form.useForm()

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  const [createdApiKey, setCreatedApiKey] = useState<IApiKeys | null>()
  const openModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    setIsModalVisible(true)
  }
  const closeModal = () => setIsModalVisible(false)

  const onFinish = async ({ keyName }: { keyName: string }) => {
    try {
      const apiKeys = await handleCreateKeys(keyName)
      setCreatedApiKey(apiKeys)
    } catch (error) {
      message.error(t("developers.APIKeys.createKeysError"))
    }
  }

  const handleClose = () => {
    if (createdApiKey) {
      fetchCredentials()
    }
    setCreatedApiKey(null)
    form.resetFields()
    closeModal()
  }

  return (
    <>
      {React.cloneElement(button, { onClick: openModal })}
      <ModalCard closable isModalVisible={isModalVisible} onCancel={handleClose} title={title}>
        <Form className="flex flex-col" id={"create-api-keys-form"} form={form} onFinish={onFinish}>
          {/* This title is a bit redundant */}
          {/* <Title level={5}>{t("common.apiSecret")}</Title> */}
          {
            <>
              <SemiBoldLabelText>{t("developers.APIKeysAddModal.keyName")}</SemiBoldLabelText>
              <Form.Item
                id="keyName"
                name="keyName"
                rules={[
                  {
                    required: true,
                    message: t("common.fieldRequired"),
                  },
                ]}
              >
                <Input
                  prefix=" "
                  className="font-normal rounded-md"
                  disabled={!isEmpty(createdApiKey)}
                />
              </Form.Item>
              {!isEmpty(createdApiKey) && (
                <div className="flex flex-col space-y-4">
                  <div className="flex w-full px-4 py-2 space-x-2 bg-[#F9FAFB]">
                    <FontAwesomeIcon
                      className="text-gray-600 pt-1"
                      size="lg"
                      icon={faExclamationCircle}
                    />
                    <p id={"save-key-tips-text"}>{t("developers.APIKeys.saveSecretKeyMessage")}</p>
                  </div>
                  <div className="flex w-full rounded border px-4 py-3">
                    <Paragraph
                      id={"api-secret-key"}
                      className="text-gray-400 text-base break-all"
                      copyable={{
                        icon: (
                          <FontAwesomeIcon
                            className="text-gray-500 text-md cursor-pointer"
                            icon={faClone}
                          />
                        ),
                      }}
                    >
                      {createdApiKey?.secretKey}
                    </Paragraph>
                  </div>
                </div>
              )}
            </>
          }
          <div className="flex w-full justify-end !mt-4 space-x-4">
            {isEmpty(createdApiKey) ? (
              <KosmoButton id="create-api-key-form-btn" type="primary" htmlType="submit">
                {t("common.create")}
              </KosmoButton>
            ) : (
              <KosmoButton id="api-form-close-button" type="primary" onClick={handleClose}>
                {t("common.close")}
              </KosmoButton>
            )}
          </div>
        </Form>
      </ModalCard>
    </>
  )
}

export default APIKeysAddEditModal
